import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, FilePdf, PencilSimple, Plus, Trash, ArrowsClockwise, CaretRight, PaperPlaneTilt, Eye, CheckCircle } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { InvoiceFormModal } from '../invoicing/InvoiceFormModal';
import { PdfViewerModal } from '../invoicing/PdfViewerModal';
import { QuotePercentageDialog } from '../invoicing/QuotePercentageDialog';
import { QuoteConsultDialog } from '../invoicing/QuoteConsultDialog';
import ProgressChart from './ProgressChart';
import { fmtDateFR } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' },
  envoye: { label: 'Envoye', color: '#1565C0', bg: '#E3F2FD' },
  accepte: { label: 'Accepte', color: '#2E7D32', bg: '#E8F5E9' },
  refuse: { label: 'Refuse', color: '#C62828', bg: '#FFEBEE' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.brouillon;
  return <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
};

const emptyForm = (projectId, projectClientId) => ({
  project_id: projectId || '',
  client_id: projectClientId || '',
  payment_delay: 30,
  date: new Date().toISOString().slice(0, 10),
  validity_date: '',
  due_date: '',
  notes: '',
  client_name: '', client_email: '', client_phone: '', client_address: '',
  lines: [{ description: '', unit: '', unit_price: 0, quantity: 0, tva_rate: 20, monthly_advancement: 0, total_advancement: 0 }],
});

export const ProjectQuotesPanel = ({ project, onProjectFinanceChange, billingInvoices = [] }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // {parentId: bool}
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [library, setLibrary] = useState([]);
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm(project?.id));
  const [pendingAvenantParent, setPendingAvenantParent] = useState(null); // quote id when creating avenant
  const [lockClient, setLockClient] = useState(false); // lock client field when creating from project context
  const [pdfViewer, setPdfViewer] = useState({ show: false, url: '', title: '' });
  const [convertDialog, setConvertDialog] = useState({ open: false, quote: null, submitting: false, previousAdvancements: {} });
  const [consultQuote, setConsultQuote] = useState(null);

  const openConvertDialog = async (quote) => {
    let prev = {};
    try {
      const r = await axios.get(`${API_URL}/api/invoicing/quotes/${quote.id}/advancements`, { withCredentials: true });
      prev = r.data?.previous_advancements || {};
    } catch { /* on 401 etc, fallback to empty */ }
    setConvertDialog({ open: true, quote, submitting: false, previousAdvancements: prev });
  };
  const handleConvertConfirm = async (lineAdvancements) => {
    const q = convertDialog.quote;
    if (!q) return;
    setConvertDialog(d => ({ ...d, submitting: true }));
    try {
      const res = await axios.post(
        `${API_URL}/api/invoicing/quotes/${q.id}/convert`,
        { line_advancements: lineAdvancements },
        { withCredentials: true }
      );
      toast.success(`Facture ${res.data.number} creee`);
      setConvertDialog({ open: false, quote: null, submitting: false, previousAdvancements: {} });
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur conversion');
      setConvertDialog(d => ({ ...d, submitting: false }));
    }
  };

  const handleSolder = async (quote) => {
    if (!quote) return;
    if (!window.confirm(`Solder le devis ${quote.number} ?\nLa facture sera creee avec le pourcentage restant a facturer (100% - cumul actuel).`)) return;
    try {
      const r = await axios.get(`${API_URL}/api/invoicing/quotes/${quote.id}/advancements`, { withCredentials: true });
      const prev = r.data?.previous_advancements || {};
      // Backend expects cumulative target %, not delta. For Solder we always
      // target 100% on each line that is not yet fully invoiced.
      const lineAdvancements = {};
      let nbToInvoice = 0;
      (quote.lines || []).forEach(ln => {
        const cum = Number(prev[ln.id] || 0);
        if (cum < 100) {
          lineAdvancements[ln.id] = 100;
          nbToInvoice += 1;
        } else {
          lineAdvancements[ln.id] = cum; // no delta
        }
      });
      if (nbToInvoice === 0) {
        toast.warning('Le devis est deja soldé (100% facture sur tous les articles).');
        return;
      }
      const res = await axios.post(
        `${API_URL}/api/invoicing/quotes/${quote.id}/convert`,
        { line_advancements: lineAdvancements },
        { withCredentials: true }
      );
      toast.success(`Facture solde ${res.data.number} creee`);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du soldage');
    }
  };


  const fetchAll = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const [quotesRes, clientsRes, projRes, libRes] = await Promise.all([
        axios.get(`${API_URL}/api/invoicing/quotes`, { params: { project_id: project.id }, withCredentials: true }),
        axios.get(`${API_URL}/api/contacts?category=client`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/library`, { withCredentials: true }),
      ]);
      setQuotes(quotesRes.data || []);
      setClients(clientsRes.data || []);
      setProjects(projRes.data || []);
      setLibrary(libRes.data || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  const refreshAll = useCallback(async () => {
    await fetchAll();
    if (typeof onProjectFinanceChange === 'function') {
      onProjectFinanceChange();
    }
  }, [fetchAll, onProjectFinanceChange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Group quotes: roots (no parent) and children (avenants) per root
  const roots = quotes.filter(q => !q.is_avenant);
  const avenantsByParent = {};
  quotes.filter(q => q.is_avenant).forEach(a => {
    if (!avenantsByParent[a.parent_quote_id]) avenantsByParent[a.parent_quote_id] = [];
    avenantsByParent[a.parent_quote_id].push(a);
  });
  Object.values(avenantsByParent).forEach(arr => arr.sort((a, b) => (a.avenant_number || 0) - (b.avenant_number || 0)));

  // Avenant totals across all quotes of this project
  const allAvenants = quotes.filter(q => q.is_avenant);
  const avenantsTotalAllHT = allAvenants.reduce((s, a) => s + (a.total_ht || 0), 0);
  const avenantsTotalAllTTC = allAvenants.reduce((s, a) => s + (a.total_ttc || 0), 0);
  const acceptedAvenants = allAvenants.filter(a => a.status === 'accepte');
  const avenantsTotalAcceptedHT = acceptedAvenants.reduce((s, a) => s + (a.total_ht || 0), 0);
  const avenantsTotalAcceptedTTC = acceptedAvenants.reduce((s, a) => s + (a.total_ttc || 0), 0);

  // ---- Actions ----
  const openEdit = (item) => {
    setEditingItem(item);
    setPendingAvenantParent(null);
    setForm({
      project_id: item.project_id || '',
      client_id: item.client_id || '',
      payment_delay: 'custom',
      date: item.date,
      validity_date: item.validity_date || '',
      due_date: '',
      notes: item.notes || '',
      client_name: item.client_name || '', client_email: item.client_email || '', client_phone: item.client_phone || '', client_address: item.client_address || '',
      lines: (item.lines || []).map(ln => ({ id: ln.id, kind: ln.kind || 'item', description: ln.description, details: ln.details || '', unit: ln.unit, unit_price: ln.unit_price, quantity: ln.quantity, tva_rate: ln.tva_rate, monthly_advancement: ln.monthly_advancement || 0, total_advancement: ln.total_advancement || 0 })),
    });
    setShowModal(true);
  };

  const openCreateAvenant = (parentQuote) => {
    setEditingItem(null);
    setPendingAvenantParent(parentQuote);
    setForm({
      project_id: parentQuote.project_id || '',
      client_id: parentQuote.client_id || '',
      payment_delay: 'custom',
      date: new Date().toISOString().slice(0, 10),
      validity_date: parentQuote.validity_date || '',
      due_date: '',
      notes: '',
      client_name: parentQuote.client_name || '', client_email: parentQuote.client_email || '', client_phone: parentQuote.client_phone || '', client_address: parentQuote.client_address || '',
      // Empty by default — user types only delta lines
      lines: [{ description: '', unit: '', unit_price: 0, quantity: 0, tva_rate: 20, monthly_advancement: 0, total_advancement: 0 }],
    });
    setShowModal(true);
  };

  const openCreateNewQuote = () => {
    setEditingItem(null);
    setPendingAvenantParent(null);
    // Pre-fill client: prefer project.client_id (exact match), then fallback to fuzzy name match
    const clientFromId = project.client_id ? clients.find(c => c.id === project.client_id) : null;
    const clientFromName = clientFromId || clients.find(c => (c.name || '').toLowerCase() === (project.client_name || '').toLowerCase());
    const resolvedClient = clientFromId || clientFromName;
    const resolvedClientId = resolvedClient?.id || '';
    setLockClient(!!resolvedClientId); // only lock if a client could be auto-resolved
    setForm({
      ...emptyForm(project.id, resolvedClientId),
      client_name: resolvedClient?.name || project.client_name || '',
      client_email: resolvedClient?.email || '',
      client_phone: resolvedClient?.phone || '',
      client_address: resolvedClient?.address || project.address || '',
    });
    setShowModal(true);
  };

  const openCreateAvenantWithLock = (parentQuote) => {
    setLockClient(true); // avenant always inherits client from parent
    openCreateAvenant(parentQuote);
  };

  const openEditWithLock = (item) => {
    setLockClient(false); // allow editing client when modifying an existing quote
    openEdit(item);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validLines = form.lines.filter(ln => ln.description.trim());
    if (validLines.length === 0) return toast.error('Ajoutez au moins un poste');
    if (!editingItem && !pendingAvenantParent && !form.client_id) {
      return toast.error('Selectionnez un client dans le repertoire');
    }
    const payload = {
      project_id: form.project_id || null,
      client_id: form.client_id || null,
      date: form.date,
      validity_date: form.validity_date,
      notes: form.notes,
      client_name: form.client_name,
      client_email: form.client_email,
      client_phone: form.client_phone,
      client_address: form.client_address,
      lines: validLines,
    };
    try {
      if (editingItem && editingItem._isInvoice) {
        await axios.put(`${API_URL}/api/invoicing/${editingItem.id}`, payload, { withCredentials: true });
        toast.success('Facture mise a jour');
      } else if (editingItem) {
        await axios.put(`${API_URL}/api/invoicing/quotes/${editingItem.id}`, payload, { withCredentials: true });
        toast.success('Devis mis a jour');
      } else if (pendingAvenantParent) {
        await axios.post(`${API_URL}/api/invoicing/quotes/${pendingAvenantParent.id}/avenants`, { lines: validLines, notes: form.notes, validity_date: form.validity_date, date: form.date }, { withCredentials: true });
        toast.success(`Avenant cree pour ${pendingAvenantParent.number}`);
      } else {
        await axios.post(`${API_URL}/api/invoicing/quotes`, payload, { withCredentials: true });
        toast.success('Devis cree');
      }
      setShowModal(false);
      setPendingAvenantParent(null);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/invoicing/quotes/${id}`, { status: newStatus }, { withCredentials: true });
      toast.success('Statut mis a jour');
      refreshAll();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id, isAvenant) => {
    const label = isAvenant ? 'cet avenant' : 'ce devis et tous ses avenants';
    if (!window.confirm(`Supprimer ${label} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/invoicing/quotes/${id}`, { withCredentials: true });
      // Cascade for parent: also delete its children avenants client-side request
      if (!isAvenant) {
        const children = avenantsByParent[id] || [];
        await Promise.all(children.map(c => axios.delete(`${API_URL}/api/invoicing/quotes/${c.id}`, { withCredentials: true })));
      }
      toast.success('Supprime');
      refreshAll();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleViewPdf = async (item) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoicing/quotes/${item.id}/export/pdf`, { withCredentials: true, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const title = item.is_avenant ? `Avenant ${item.number}` : `Devis ${item.number}`;
      setPdfViewer({ show: true, url: blobUrl, title });
    } catch (e) {
      toast.error('Impossible de charger le PDF');
    }
  };

  const handleViewInvoicePdf = async (inv) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoicing/${inv.id}/export/pdf`, { withCredentials: true, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfViewer({ show: true, url: blobUrl, title: `Facture ${inv.number}` });
    } catch { toast.error('Impossible de charger le PDF'); }
  };

  const handleInvoiceStatus = async (inv, status) => {
    try {
      await axios.put(`${API_URL}/api/invoicing/${inv.id}`, { status }, { withCredentials: true });
      toast.success('Statut mis à jour');
      refreshAll();
    } catch { toast.error('Erreur'); }
  };

  const handleInvoiceDelete = async (inv) => {
    // Force delete : autorisé même pour les factures émises/payées (à la demande de l'utilisateur).
    // On affiche un avertissement explicite pour les statuts post-émission.
    const isIssued = inv.status && !['brouillon', 'annulee'].includes(inv.status);
    const warn = isIssued
      ? `ATTENTION : la facture ${inv.number} a le statut "${inv.status}". La supprimer va contourner l'obligation légale (un avoir serait normalement requis). Confirmer la suppression définitive ?`
      : `Supprimer la facture ${inv.number} ?`;
    if (!window.confirm(warn)) return;
    try {
      await axios.delete(`${API_URL}/api/invoicing/${inv.id}`, { withCredentials: true });
      toast.success('Facture supprimée');
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur suppression'); }
  };

  const handleCreateCreditNote = async (inv) => {
    const reason = window.prompt(
      `Motif de l'avoir sur ${inv.number} (obligation légale) :`,
      'Erreur de facturation'
    );
    if (!reason || !reason.trim()) {
      toast.error('Motif requis');
      return;
    }
    try {
      const r = await axios.post(`${API_URL}/api/invoicing/${inv.id}/credit-note`, { reason: reason.trim(), partial: false }, { withCredentials: true });
      toast.success(`Avoir ${r.data.number} créé (${(r.data.total_ttc || 0).toLocaleString('fr-FR')} € TTC)`);
      refreshAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur création avoir'); }
  };

  const closePdfViewer = () => {
    if (pdfViewer.url) URL.revokeObjectURL(pdfViewer.url);
    setPdfViewer({ show: false, url: '', title: '' });
  };

  const handleSendInvoice = async (inv) => {
    if (!inv.client_email && !window.confirm("Aucun email client renseigne sur cette facture. Continuer ?")) return;
    if (!window.confirm(`Envoyer la facture ${inv.number} par email ?`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/invoicing/${inv.id}/send-to-client`, {}, { withCredentials: true });
      toast.success(`Envoye a ${res.data.to || 'client'}`);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Echec de l'envoi");
    }
  };

  const handleEditInvoice = (inv) => {
    setEditingItem({ ...inv, _isInvoice: true });
    setPendingAvenantParent(null);
    setLockClient(false);
    setForm({
      project_id: inv.project_id || '',
      client_id: inv.client_id || '',
      payment_delay: 'custom',
      date: inv.date,
      validity_date: '',
      due_date: inv.due_date || '',
      notes: inv.notes || '',
      client_name: inv.client_name || '', client_email: inv.client_email || '', client_phone: inv.client_phone || '', client_address: inv.client_address || '',
      lines: (inv.lines || []).map(ln => ({ id: ln.id, kind: ln.kind || 'item', description: ln.description, details: ln.details || '', unit: ln.unit, unit_price: ln.unit_price, quantity: ln.quantity, tva_rate: ln.tva_rate, monthly_advancement: ln.monthly_advancement || 0, total_advancement: ln.total_advancement || 0 })),
    });
    setShowModal(true);
  };

  const handleSendToClient = async (item) => {
    if (!item.client_email && !window.confirm("Aucun email client n'est renseigne sur ce document. Continuer quand meme ?")) return;
    const label = item.is_avenant ? `l'avenant ${item.number}` : `le devis ${item.number}`;
    if (!window.confirm(`Envoyer ${label} par email au client ?`)) return;
    try {
      const res = await axios.post(`${API_URL}/api/invoicing/quotes/${item.id}/send-to-client`, {}, { withCredentials: true });
      toast.success(`Envoye a ${res.data.to}`);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec de l'envoi");
    }
  };

  const openConvertDialogPlaceholder = null; // replaced above

  // Modal helpers (same API as InvoicingPage)
  const addLine = () => setForm({ ...form, lines: [...form.lines, { description: '', details: '', unit: '', unit_price: 0, quantity: 0, tva_rate: 20, monthly_advancement: 0, total_advancement: 0 }] });
  const removeLine = (idx) => { if (form.lines.length <= 1) return; setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) }); };
  const updateLine = (idx, field, value) => { const ls = [...form.lines]; ls[idx] = { ...ls[idx], [field]: value }; setForm({ ...form, lines: ls }); };
  const addFromLibrary = (libItem) => { setForm({ ...form, lines: [...form.lines, { description: libItem.description, unit: libItem.unit, unit_price: libItem.unit_price, quantity: 0, tva_rate: libItem.tva_rate, monthly_advancement: 0, total_advancement: 0 }] }); };
  const calcLineHT = (ln) => (ln.unit_price || 0) * (ln.quantity || 0);
  const calcLineTTC = (ln) => calcLineHT(ln) * (1 + (ln.tva_rate || 20) / 100);
  const calcAdvHT = (ln) => calcLineHT(ln) * (ln.total_advancement || 0) / 100;

  if (loading) return <div style={{ padding: '24px', textAlign: 'center', color: '#5E5F66' }}>Chargement des devis…</div>;

  return (
    <div data-testid="project-quotes-panel">
      {/* Bandeau BdC + Retenue rappel - visible uniquement si applicable */}
      {(project?.contract_type === 'bon_commande' || project?.guarantee_type === 'retenue') && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }} data-testid="commercial-banners">
          {project?.contract_type === 'bon_commande' && (
            <div style={{ padding: '10px 14px', background: project?.purchase_order_doc_id ? '#ECFDF5' : '#FEF3C7', border: `1px solid ${project?.purchase_order_doc_id ? '#10B981' : '#F59E0B'}`, borderRadius: 8, flex: '1 1 280px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: project?.purchase_order_doc_id ? '#065F46' : '#92400E' }}>
                  Bon de commande {project?.purchase_order_number ? `N° ${project.purchase_order_number}` : '(numero a renseigner)'}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: 2 }}>
                  {project?.purchase_order_doc_id ? 'PDF annexe a chaque facture' : 'PDF du bon a ajouter (onglet Gestion)'}
                </div>
              </div>
            </div>
          )}
          {project?.guarantee_type === 'retenue' && (
            <div style={{ padding: '10px 14px', background: '#FFF7ED', border: '1px solid #F97316', borderRadius: 8, flex: '1 1 280px' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#9A3412' }}>Retenue de garantie {project?.retention_percent || 5}% active</div>
              <div style={{ fontSize: '0.72rem', color: '#5E5F66', marginTop: 2 }}>Deduite sur chaque facture · Cumul actuel : <strong>{(Number(project?.total_retention_ht || 0)).toLocaleString('fr-FR')} EUR HT</strong></div>
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Devis & avenants</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#5E5F66' }}>{roots.length} devis principal(aux) - {Object.values(avenantsByParent).flat().length} avenant(s)</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateNewQuote} data-testid="quote-new-btn"><Plus size={16} /> Nouveau devis</button>
      </div>

      {roots.length === 0 ? (
        <div className="card" style={{ padding: '32px', textAlign: 'center', color: '#5E5F66' }}>
          <FileText size={42} style={{ opacity: 0.4, marginBottom: '8px' }} />
          <p style={{ margin: 0 }}>Aucun devis sur ce chantier.</p>
          <p style={{ fontSize: '0.85rem', margin: '4px 0 0' }}>Cliquez sur "Nouveau devis" pour commencer.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {roots.map(q => {
            const avs = avenantsByParent[q.id] || [];
            const isOpen = expanded[q.id] !== false; // open by default
            return (
              <div key={q.id} className="card" style={{ padding: '14px 18px' }} data-testid={`quote-row-${q.id}`}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', cursor: 'pointer' }}
                  onClick={() => setConsultQuote(q)}
                  title="Cliquer pour consulter le devis"
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}
                    onClick={(e) => { /* parent handler already fires */ }}
                  >
                    {avs.length > 0 && (
                      <button onClick={() => setExpanded(prev => ({ ...prev, [q.id]: !isOpen }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
                        title={isOpen ? 'Masquer les avenants' : 'Afficher les avenants'}
                        data-testid={`toggle-avenants-${q.id}`}>
                        <CaretRight size={14} weight="bold" style={{ transition: 'transform 0.18s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0)' }} />
                      </button>
                    )}
                    <span style={{ fontWeight: 700, color: '#002FA7', fontSize: '0.95rem' }}>{q.number}</span>
                    <span style={{ fontSize: '0.85rem', color: '#5E5F66' }}>{q.date}</span>
                    <span style={{ fontSize: '0.85rem' }}>{q.lines.length} poste(s)</span>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2E7D32' }}>{(q.total_ttc || 0).toLocaleString('fr-FR')} EUR TTC</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                    <Select value={q.status} onValueChange={(v) => handleStatusChange(q.id, v)}>
                      <SelectTrigger style={{ width: '140px', height: '32px' }} data-testid={`quote-status-${q.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <StatusBadge status={q.status} />
                    <button className="btn btn-secondary btn-sm" onClick={() => handleViewPdf(q)} title="Voir le PDF" data-testid={`quote-pdf-${q.id}`}><FilePdf size={14} /></button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleSendToClient(q)} title="Envoyer au client par email" data-testid={`quote-send-${q.id}`}><PaperPlaneTilt size={14} /> Envoyer</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEditWithLock(q)} title="Modifier" data-testid={`quote-edit-${q.id}`}><PencilSimple size={14} /> Modifier</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => openCreateAvenantWithLock(q)} title="Creer un avenant" data-testid={`quote-add-avenant-${q.id}`}><Plus size={12} /> Avenant</button>
                    {q.status !== 'refuse' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => openConvertDialog(q)}
                        title="Creer une facture en choisissant un % d'avancement par article"
                        data-testid={`quote-convert-${q.id}`}
                      >
                        <ArrowsClockwise size={14} /> Facturer
                      </button>
                    )}
                    {q.status !== 'refuse' && (
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleSolder(q)}
                        title="Solder : facturer le pourcentage restant a 100%"
                        data-testid={`quote-solder-${q.id}`}
                        style={{ background: '#047857', color: 'white', borderColor: '#047857' }}
                      >
                        <CheckCircle size={14} /> Solder
                      </button>
                    )}
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(q.id, false)} title="Supprimer"><Trash size={14} /></button>
                  </div>
                </div>

                {avs.length > 0 && isOpen && (
                  <div style={{ marginTop: '12px', borderTop: '1px solid var(--border, #E2E2E6)', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {avs.map(a => (
                      <div
                        key={a.id}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', padding: '8px 12px', background: 'var(--bg-soft, #F8F9FB)', borderRadius: '8px', cursor: 'pointer' }}
                        data-testid={`avenant-row-${a.id}`}
                        onClick={() => {
                          const fullAv = quotes.find(x => x.id === a.id);
                          setConsultQuote(fullAv || a);
                        }}
                        title="Cliquer pour consulter l'avenant"
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#7A7B82', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avenant N°{a.avenant_number}</span>
                          <span style={{ fontWeight: 700, color: '#002FA7', fontSize: '0.95rem' }}>{a.number}</span>
                          <span style={{ fontSize: '0.85rem', color: '#5E5F66' }}>{a.date}</span>
                          <span style={{ fontSize: '0.85rem' }}>{a.lines_count ?? a.lines?.length ?? 0} poste(s)</span>
                          <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2E7D32' }}>{(a.total_ttc || 0).toLocaleString('fr-FR')} EUR TTC</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                          <Select value={a.status} onValueChange={(v) => handleStatusChange(a.id, v)}>
                            <SelectTrigger style={{ width: '140px', height: '32px' }} data-testid={`avenant-status-${a.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <StatusBadge status={a.status} />
                          <button className="btn btn-secondary btn-sm" onClick={() => handleViewPdf(a)} title="Voir le PDF" data-testid={`avenant-pdf-${a.id}`}><FilePdf size={14} /></button>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleSendToClient(a)} title="Envoyer au client par email" data-testid={`avenant-send-${a.id}`}><PaperPlaneTilt size={14} /> Envoyer</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => {
                            const fullAv = quotes.find(x => x.id === a.id);
                            if (fullAv) openEditWithLock(fullAv);
                          }} title="Modifier" data-testid={`avenant-edit-${a.id}`}><PencilSimple size={14} /></button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a.id, true)} title="Supprimer" data-testid={`avenant-delete-${a.id}`}><Trash size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Linked invoices + nested credit notes — full action set */}
                {(() => {
                  const linkedIds = new Set([q.id, ...avs.map(a => a.id)]);
                  const linkedInvoices = (billingInvoices || []).filter(inv => linkedIds.has(inv.quote_id));
                  if (linkedInvoices.length === 0) return null;
                  // Split into parents and avoirs; nest avoirs under their parent
                  const parents = linkedInvoices.filter(i => !i.is_credit_note);
                  const avoirsByParent = {};
                  linkedInvoices.filter(i => i.is_credit_note).forEach(av => {
                    const parentId = av.credit_note_for_id || '';
                    if (!avoirsByParent[parentId]) avoirsByParent[parentId] = [];
                    avoirsByParent[parentId].push(av);
                  });
                  // Orphan avoirs (whose parent isn't in this group) shown at the end
                  const knownParentIds = new Set(parents.map(p => p.id));
                  const orphanAvoirs = linkedInvoices.filter(i => i.is_credit_note && !knownParentIds.has(i.credit_note_for_id || ''));
                  const totalAdvHT = parents.reduce((s, i) => s + (i.total_advancement_ht || i.total_ht || 0), 0);
                  const totalAvoirsHT = linkedInvoices.filter(i => i.is_credit_note).reduce((s, i) => s + (i.total_advancement_ht || i.total_ht || 0), 0);

                  const renderInvoiceRow = (inv, isNested = false) => {
                    const cfgInv = { brouillon: { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' }, acceptee: { label: 'Acceptée', color: '#92400E', bg: '#FEF3C7' }, emise: { label: 'Émise', color: '#1565C0', bg: '#E3F2FD' }, payee: { label: 'Payée', color: '#2E7D32', bg: '#E8F5E9' }, annulee: { label: 'Annulée', color: '#C62828', bg: '#FFEBEE' } }[inv.status] || { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' };
                    return (
                      <tr key={inv.id} data-testid={`linked-invoice-${inv.id}`} style={{ background: inv.is_credit_note ? '#FEF2F2' : (inv.has_credit_note ? '#FFFBEB' : 'transparent') }}>
                        <td style={{ fontWeight: 700, color: inv.is_credit_note ? '#C62828' : '#002FA7', paddingLeft: isNested ? 26 : undefined }}>
                          {isNested && <span style={{ color: '#9CA3AF', marginRight: 4 }}>↳</span>}
                          {inv.is_credit_note && <span style={{ background: '#C62828', color: 'white', padding: '1px 6px', borderRadius: 3, fontSize: '0.65rem', marginRight: 6 }}>AVOIR</span>}
                          {inv.number}
                          {inv.has_credit_note && <div style={{ fontSize: '0.68rem', color: '#92400E', fontWeight: 600 }}>⚠ Avoir(s) émis</div>}
                          {inv.is_credit_note && inv.credit_note_reason && <div style={{ fontSize: '0.68rem', color: '#5E5F66', fontWeight: 400, fontStyle: 'italic' }}>Motif : {inv.credit_note_reason}</div>}
                        </td>
                        <td>{fmtDateFR(inv.date)}</td>
                        <td>{inv.lines_count ?? inv.lines?.length ?? 0}</td>
                        <td style={{ textAlign: 'right', color: inv.is_credit_note ? '#C62828' : 'inherit' }}>{(inv.total_advancement_ht || inv.total_ht || 0).toLocaleString('fr-FR')} €</td>
                        <td style={{ textAlign: 'right', fontWeight: 600, color: inv.is_credit_note ? '#C62828' : '#2E7D32' }}>{(inv.total_advancement_ttc || inv.total_ttc || 0).toLocaleString('fr-FR')} €</td>
                        <td>
                          {!inv.is_credit_note ? (
                            <Select value={inv.status} onValueChange={(v) => handleInvoiceStatus(inv, v)}>
                              <SelectTrigger style={{ width: 130, height: 28, fontSize: '0.78rem' }} data-testid={`linked-invoice-status-${inv.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries({ brouillon: 'Brouillon', emise: 'Émise', payee: 'Payée', annulee: 'Annulée' }).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: cfgInv.bg, color: cfgInv.color }}>{cfgInv.label}</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleViewInvoicePdf(inv)} title="Voir PDF" data-testid={`linked-invoice-pdf-${inv.id}`}><FilePdf size={13} /></button>
                            {!inv.is_credit_note && (
                              <button className="btn btn-secondary btn-sm" onClick={() => handleSendInvoice(inv)} title="Envoyer au client" data-testid={`linked-invoice-send-${inv.id}`}><PaperPlaneTilt size={13} /></button>
                            )}
                            <button className="btn btn-secondary btn-sm" onClick={() => handleEditInvoice(inv)} title="Modifier" data-testid={`linked-invoice-edit-${inv.id}`}><PencilSimple size={13} /></button>
                            {!inv.is_credit_note && inv.status !== 'brouillon' && (
                              <button className="btn btn-sm" onClick={() => handleCreateCreditNote(inv)} title="Créer un avoir" style={{ background: '#FEF3C7', color: '#92400E', borderColor: '#F59E0B' }} data-testid={`linked-invoice-credit-${inv.id}`}>↩ Avoir</button>
                            )}
                            {(inv.status === 'brouillon' || inv.is_credit_note) && (
                              <button className="btn btn-danger btn-sm" onClick={() => handleInvoiceDelete(inv)} title="Supprimer" data-testid={`linked-invoice-del-${inv.id}`}><Trash size={13} /></button>
                            )}
                            {(inv.status !== 'brouillon' && !inv.is_credit_note) && (
                              <button className="btn btn-danger btn-sm" onClick={() => handleInvoiceDelete(inv)} title="Supprimer (force)" data-testid={`linked-invoice-del-${inv.id}`}><Trash size={13} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  };

                  return (
                    <div data-testid={`linked-invoices-${q.id}`} style={{ marginTop: 12, borderTop: '1px solid #E2E2E6', paddingTop: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#7A7B82', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Situations / Factures rattachées ({parents.length}{Object.keys(avoirsByParent).length > 0 ? ` + ${linkedInvoices.length - parents.length} avoir(s)` : ''})
                        </span>
                        <span style={{ fontSize: '0.82rem', color: '#5E5F66' }}>
                          Total facturé : <strong style={{ color: '#2E7D32' }}>{totalAdvHT.toLocaleString('fr-FR')} € HT</strong>
                          {totalAvoirsHT !== 0 && <> · Avoirs : <strong style={{ color: '#C62828' }}>{totalAvoirsHT.toLocaleString('fr-FR')} € HT</strong></>}
                        </span>
                      </div>
                      <table className="data-table" style={{ fontSize: '0.85rem' }}>
                        <thead><tr>
                          <th>N° Facture</th><th>Date</th><th>Lignes</th>
                          <th style={{ textAlign: 'right' }}>HT</th>
                          <th style={{ textAlign: 'right' }}>TTC</th>
                          <th>Statut</th><th></th>
                        </tr></thead>
                        <tbody>
                          {parents.map(parent => (
                            <React.Fragment key={parent.id}>
                              {renderInvoiceRow(parent, false)}
                              {(avoirsByParent[parent.id] || []).map(av => renderInvoiceRow(av, true))}
                            </React.Fragment>
                          ))}
                          {orphanAvoirs.map(av => renderInvoiceRow(av, false))}
                        </tbody>
                      </table>
                      {/* Worksite progress chart : actual cumulative billed vs planned timeline */}
                      <div style={{ marginTop: 14 }}>
                        <ProgressChart quoteId={q.id} />
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* Orphan invoices : not linked to any quote (legacy or manual) */}
      {(() => {
        const allQuoteIds = new Set(quotes.map(q => q.id));
        const orphans = (billingInvoices || []).filter(inv => !inv.quote_id || !allQuoteIds.has(inv.quote_id));
        if (orphans.length === 0) return null;
        const totalOrphanHT = orphans.reduce((s, i) => s + (i.total_advancement_ht || i.total_ht || 0), 0);
        return (
          <div data-testid="orphan-invoices-card" className="card" style={{ marginTop: 14, padding: '14px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#92400E' }}>Factures hors devis ({orphans.length})</h4>
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#5E5F66' }}>Factures non rattachées à un devis de ce chantier.</p>
              </div>
              <span style={{ fontSize: '0.85rem' }}>Total : <strong style={{ color: '#2E7D32' }}>{totalOrphanHT.toLocaleString('fr-FR')} € HT</strong></span>
            </div>
            <table className="data-table" style={{ fontSize: '0.85rem' }}>
              <thead><tr>
                <th>N° Facture</th><th>Date</th><th>Lignes</th>
                <th style={{ textAlign: 'right' }}>Avancement HT</th>
                <th style={{ textAlign: 'right' }}>Avancement TTC</th>
                <th>Statut</th><th></th>
              </tr></thead>
              <tbody>
                {orphans.map(inv => {
                  const cfgInv = { brouillon: { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' }, acceptee: { label: 'Acceptée', color: '#92400E', bg: '#FEF3C7' }, emise: { label: 'Émise', color: '#1565C0', bg: '#E3F2FD' }, payee: { label: 'Payée', color: '#2E7D32', bg: '#E8F5E9' }, annulee: { label: 'Annulée', color: '#C62828', bg: '#FFEBEE' } }[inv.status] || { label: 'Brouillon', color: '#5E5F66', bg: '#F4F5F7' };
                  return (
                    <tr key={inv.id} data-testid={`orphan-invoice-${inv.id}`}>
                      <td style={{ fontWeight: 700, color: '#002FA7' }}>{inv.number}</td>
                      <td>{inv.date}</td>
                      <td>{inv.lines_count ?? inv.lines?.length ?? 0}</td>
                      <td style={{ textAlign: 'right' }}>{(inv.total_advancement_ht || inv.total_ht || 0).toLocaleString('fr-FR')} €</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: '#2E7D32' }}>{(inv.total_advancement_ttc || inv.total_ttc || 0).toLocaleString('fr-FR')} €</td>
                      <td><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: cfgInv.bg, color: cfgInv.color }}>{cfgInv.label}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleViewInvoicePdf(inv)} title="Voir PDF"><FilePdf size={13} /></button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleInvoiceDelete(inv)} title="Supprimer"><Trash size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {allAvenants.length > 0 && (
        <div data-testid="avenants-totals-summary" className="card" style={{ marginTop: '14px', padding: '14px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.6px', color: '#5E5F66', fontWeight: 700 }}>Total avenants</div>
              <div style={{ fontSize: '0.75rem', color: '#7A7B82', marginTop: '2px' }}>{allAvenants.length} avenant(s) au total - {acceptedAvenants.length} accepte(s)</div>
            </div>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>Tous statuts</div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{avenantsTotalAllHT.toLocaleString('fr-FR')} EUR HT</div>
                <div style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{avenantsTotalAllTTC.toLocaleString('fr-FR')} EUR TTC</div>
              </div>
              <div style={{ textAlign: 'right', borderLeft: '1px solid var(--border, #E2E2E6)', paddingLeft: '24px' }}>
                <div style={{ fontSize: '0.72rem', color: '#2E7D32', fontWeight: 600 }}>Acceptes (comptes au prix de vente)</div>
                <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#2E7D32' }}>+ {avenantsTotalAcceptedHT.toLocaleString('fr-FR')} EUR HT</div>
                <div style={{ fontSize: '0.78rem', color: '#2E7D32' }}>+ {avenantsTotalAcceptedTTC.toLocaleString('fr-FR')} EUR TTC</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <InvoiceFormModal
        show={showModal}
        onClose={() => { setShowModal(false); setPendingAvenantParent(null); }}
        onSubmit={handleSubmit}
        form={form}
        setForm={setForm}
        projects={projects}
        clients={clients}
        library={library}
        modalType={editingItem?._isInvoice ? "facture" : "devis"}
        editingItem={editingItem || (pendingAvenantParent ? { number: `Avenant pour ${pendingAvenantParent.number}` } : null)}
        addLine={addLine}
        removeLine={removeLine}
        updateLine={updateLine}
        addFromLibrary={addFromLibrary}
        calcLineHT={calcLineHT}
        calcLineTTC={calcLineTTC}
        calcAdvHT={calcAdvHT}
        lockProject={true}
        lockClient={lockClient}
        forceTvaZero={!!project?.autoliquidation_tva}
      />
      <PdfViewerModal show={pdfViewer.show} url={pdfViewer.url} title={pdfViewer.title} onClose={closePdfViewer} />
      {convertDialog.open && convertDialog.quote && (
        <QuotePercentageDialog
          quote={convertDialog.quote}
          previousAdvancements={convertDialog.previousAdvancements}
          onClose={() => setConvertDialog({ open: false, quote: null, submitting: false, previousAdvancements: {} })}
          onConfirm={handleConvertConfirm}
          submitting={convertDialog.submitting}
        />
      )}
      {consultQuote && (
        <QuoteConsultDialog
          quote={consultQuote}
          onClose={() => setConsultQuote(null)}
          onPdf={() => { handleViewPdf(consultQuote); }}
          onSend={() => { handleSendToClient(consultQuote); }}
          onEdit={() => {
            const full = quotes.find(x => x.id === consultQuote.id) || consultQuote;
            setConsultQuote(null);
            openEditWithLock(full);
          }}
          onConvert={consultQuote.status !== 'refuse' ? () => {
            const full = quotes.find(x => x.id === consultQuote.id) || consultQuote;
            setConsultQuote(null);
            openConvertDialog(full);
          } : null}
        />
      )}
    </div>
  );
};

export default ProjectQuotesPanel;
